import _ from 'lodash-extended';
import { TimeRectangle } from './grid';
import app from 'app';
import fg from '#/libs/FrameGrid';
import PictureInPictureAdapter from 'c/PictureInPictureAdapter';
import { assignDurations, getAudioLimit } from './tapi';
import { assetIsExpired } from './asset';

export class TimeLayerRow {
  constructor({ gridStartTime, gridDuration, gridEndTime }) {
    this.rowId = app.guid();
    this.gridStartTime = gridStartTime;
    this.gridDuration = gridDuration;
    this.gridEndTime = gridEndTime;
    this.rectangle = null;
  }

  setRectangle(x, y, width, height) {
    this.rectangle = new TimeRectangle(
      x,
      y,
      width,
      height,
      this.gridStartTime,
      this.gridEndTime
    );
  }
}

/**
 * @param asset {object}
 * @returns {object} overlay
 */
export function createOverlayFromExternalAsset(asset) {
  const overlay = _.cloneDeep(asset);
  overlay.guid = app.guid();
  overlay.limit = app.timelineGateway.getTrackLimits(overlay);
  return overlay;
}

export class AudioTrackWrapper {
  constructor({ selected, audioOnTrack }) {
    // establish two directional discoverability by guid
    audioOnTrack.guid =
      audioOnTrack.guid ?? app.guid(selected.asset.metadata.assetUUID);

    // initiate data with everything we know about selected audio from lightbox
    this.data = Object.assign(
      _.cloneDeep(selected.asset),
      _.pick(selected, ['cdnUrl'])
    );
    // update data with everything we know about it's disposition on timeline
    Object.assign(
      this.data,
      _.pick(audioOnTrack, [
        'guid',
        'displayStartTime',
        'displayEndTime',
        'relativeStartTime',
        'relativeEndTime',
      ])
    );

    AudioTrackWrapper._setDataReactiveFields(this.data);

    this.durationLimits = getAudioLimit(this.data);

    if (null === this.data.relativeStartTime) {
      this.data.relativeStartTime = 0;
    }
    if (null === this.data.relativeEndTime) {
      this.data.relativeEndTime = this.durationLimits.max;
    }

    // trek coordinates
    this.rectangles = [];
    this.isResizing = false;
    this.isMoving = false;
    this.resizeEscort = {
      fromLeft: false,
    };
  }

  // readonly
  get guid() {
    return this.data.guid;
  }

  static _setDataReactiveFields(data) {
    assignDurations(data, data);

    const reactiveDataFields = [
      'displayStartTime',
      'displayEndTime',
      'relativeStartTime',
      'relativeEndTime',
    ];

    for (let n = 0, N = reactiveDataFields.length; n < N; n++) {
      if (!Object.getOwnPropertyDescriptor(data, reactiveDataFields[n])) {
        data[reactiveDataFields[n]] = null;
      }
    }
  }

  clone() {
    const rv = new this.constructor({
      selected: {
        asset: this.data,
      },
      audioOnTrack: {
        guid: app.guid(this.data.metadata.assetUUID),
      },
    });

    return rv;
  }
}

/**
 * @param asset | assetCopy
 */
export class VisualTrackWrapper {
  constructor({ asset, assetCopy }) {
    this.data = assetCopy ?? _.cloneDeep(asset);

    this.data.guid = this.data.guid ?? app.guid(this.data.id);
    this.data.limit = app.timelineGateway.getTrackLimits(this.data);

    VisualTrackWrapper._setDataReactiveFields(this.data);

    this.pipa = new PictureInPictureAdapter({ scene: this.data });
    _.each(this.pipa.track, (overlayAsset) => {
      overlayAsset.limit = app.timelineGateway.getTrackLimits(overlayAsset);
    });

    // track coordinates
    this.rectangles = [];
    // cached styleObj for wallpaper asset
    this.colourCardCss = null;

    // states of the asset
    this.isExpired = assetIsExpired(this.data);
    this.isResizing = false;
    this.resizeEscort = {
      START_TIME_SHIFT: 0,
      END_TIME_SHIFT: 0,
    };
    this.rtpState = {
      show: false,
      status: null,
      percent: null,
    };
    // object tracking runs alongside the rtp, so it needs its own flag - sharing
    // rtpState lets an rtp status clear the tracking banner and vice versa
    this.trackingState = {
      show: false,
    };
  }

  // readonly
  get guid() {
    return this.data.guid;
  }

  getDuration() {
    let videoDuration = this.data.displayDuration;

    if (app.assetType.isFRAGMENT(this.data)) {
      // account for videoSpeed
      videoDuration /= this.data.agd.videoSpeed;
    }

    return fg.trunc(videoDuration);
  }

  /**
   * ensure props exists on object for reactivity
   * @param data {object}
   */
  static _setDataReactiveFields(data) {
    assignDurations(data, data);

    const reactiveDataFields = [
      'realTimePreviewChecksum',
      'realTimePreviewResultData',
      '_realTimePreviewInProgress',
      '_tmpRealTimePreviewChecksum',
    ];

    for (let n = 0, N = reactiveDataFields.length; n < N; n++) {
      if (!Object.getOwnPropertyDescriptor(data, reactiveDataFields[n])) {
        data[reactiveDataFields[n]] = null;
      }
    }
  }
}

export const VisualGhostMode = {
  DEFAULT: 'DEFAULT',
  DEFAULT_SCENE: 'DEFAULT_SCENE',
  INSERT: 'INSERT',
  INSERT_BETWEEN: 'INSERT_BETWEEN',
  REPLACE_OVERLAY: 'REPLACE_OVERLAY',
  REPLACE_DEFAULT: 'REPLACE_DEFAULT',
  REPLACE_SCENE: 'REPLACE_SCENE',
};

export const OverlayBorderMode = {
  PASSIVE: 'p',
  DEFAULT: 'd',
  SELECTED: 's',
  REPLACE: 'r',
};

export const AudioGhostMode = {
  DEFAULT: 'DEFAULT',
  APPEND: 'APPEND',
  INSERT: 'INSERT',
};

export const RESIZE_CURSOR = {
  BOTH: 'ew-resize',
  ONLY_LEFT: 'w-resize',
  ONLY_RIGHT: 'e-resize',
  NEITHER: 'not-allowed',
};

export const AVAILABILITY_CURSOR_MAP = {
  0b00: RESIZE_CURSOR.NEITHER,
  0b01: RESIZE_CURSOR.ONLY_RIGHT,
  0b10: RESIZE_CURSOR.ONLY_LEFT,
  0b11: RESIZE_CURSOR.BOTH,
};
